/**
 * useBuddyGroupInvitations — React Query hooks for invitation operations.
 *
 * Wraps the buddyGroupInvitationService functions with React Query
 * for caching, mutations, and automatic query invalidation.
 *
 * Exports:
 *   usePendingInvitations(userProfileId) — fetch pending invitations
 *   useAcceptInvitation() — accept mutation
 *   useDeclineInvitation() — decline mutation
 *   useSendInvitation() — send invite mutation
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { buddyGroupOpsKeys } from '../api/extensions/useBuddyGroupOps';
import {
  getPendingInvitations,
  acceptInvitation,
  declineInvitation,
  sendInvitation,
  cancelInvitation,
  type PendingInvitation,
} from '../services/buddyGroupInvitationService';

// Re-export the type for consumers
export type { PendingInvitation };

/** Query key factory for invitation queries. */
export const invitationKeys = {
  all: [...buddyGroupOpsKeys.all, 'invitations'] as const,
  pending: (userId: string) =>
    [...invitationKeys.all, 'pending', userId] as const,
};

/**
 * Fetch pending buddy group invitations for the current user.
 *
 * @param userProfileId - Current user's profile ID (used for query key;
 *   backend infers user from session cookie)
 */
export function usePendingInvitations(userProfileId: string) {
  return useQuery<PendingInvitation[]>({
    queryKey: invitationKeys.pending(userProfileId),
    queryFn: getPendingInvitations,
    enabled: !!userProfileId,
    staleTime: 60 * 1000, // 1 minute
    refetchInterval: 60 * 1000, // poll every 60s for new invitations
  });
}

/**
 * Accept a buddy group invitation.
 * On success, invalidates all buddy group queries to refresh lists.
 */
export function useAcceptInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) => acceptInvitation(invitationId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: buddyGroupOpsKeys.all });
      // Joining a buddy group can change which registrations the
      // dashboard surfaces (group-attached entries, partner seats
      // visible via group membership), so refresh the dashboard
      // queries too — otherwise the new state isn't visible until
      // a hard reload.
      qc.invalidateQueries({ queryKey: ['dashboard-data'] });
      qc.invalidateQueries({ queryKey: ['registration-ops'] });
    },
  });
}

/**
 * Decline a buddy group invitation.
 * On success, invalidates all buddy group queries to refresh lists.
 */
export function useDeclineInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) => declineInvitation(invitationId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: buddyGroupOpsKeys.all });
      // Symmetric with accept: declining should reflect on the
      // dashboard immediately so the pending-invitation indicator
      // and any group-derived entries clear out.
      qc.invalidateQueries({ queryKey: ['dashboard-data'] });
      qc.invalidateQueries({ queryKey: ['registration-ops'] });
    },
  });
}

/**
 * Send a buddy group invitation to a user.
 * On success, invalidates all buddy group queries to refresh lists.
 */
export function useSendInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      buddyGroupId,
      inviteeProfileId,
    }: {
      buddyGroupId: string;
      inviteeProfileId: string;
    }) => sendInvitation(buddyGroupId, inviteeProfileId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: buddyGroupOpsKeys.all });
    },
  });
}

/**
 * Cancel/revoke a pending invitation.
 * Used by the primary cardholder to remove a pending member from the group.
 */
export function useCancelInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) => cancelInvitation(invitationId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: buddyGroupOpsKeys.all });
    },
  });
}
